import axios from 'axios';
import https from 'https';
import cookie from 'react-cookies';
import { getCityFromHostName } from '~/src/helpers/url';
import { CITYDOMAIN_COOKIE } from '~/src/constants';
import { sendRequestByConnection } from '~/src/api/service-request/request-by-connection';
import contentRequest from '~/src/api/requests/contentRequest';
import { captureException } from '@sentry/node';
import { getPacketChannelsList } from '~/src/api/bundles';
import { getWithConvertedPrices } from '~/src/models/price';

export default class TariffAPI {
    baseURL;
    pageModule;
    alias;
    axios;

    /**
     * Устанавливаем host API
     * @param {string=} hostname - perm.dom.ru
     * @returns {string}
     */
    _setBaseURL = hostname => {
        const city = getCityFromHostName(hostname) || cookie.load(CITYDOMAIN_COOKIE) || 'perm';
        // Если локальная ветка или тестовая ветка
        if ((/t2\.ertelecom\.ru|localhost/).test(hostname)) {
            this.baseURL = `https://${city}.master.b2c-domru.web.t2.ertelecom.ru/`;
        } else {
            this.baseURL = `https://${city}.dom.ru/`;
        }
    };

    /**
     * Устанавливаем host API
     * @param {string} pageModule - internet, bundles.
     * @param {string} alias - one, xxl.
     * @param {string} hostname - perm.dom.ru
     */
    constructor({ pageModule, alias, hostname }) {
        this.pageModule = pageModule;
        this.alias = alias;
        this._setBaseURL(hostname);
        this.axios = axios.create({
            baseURL: this.baseURL,
            // если тестовые ветки, что бы решить проблему с SSl сертификатом
            httpsAgent: new https.Agent({
                rejectUnauthorized: false,
            }),
        });
    }

    getRouters = cityId => contentRequest.getWithCache(`v1/city/router-classes?cityId=${cityId}`)
        .then(response => getWithConvertedPrices(response.data))
        .catch(err => captureException(err));

    getAdditionalServices = (cityDomain, tariffId) => contentRequest.getWithCache(`${cityDomain}/v1/additional-services?filter[sale_package_id]=${tariffId}`)
        .then(response => response.data.items)
        .catch(err => captureException(err));

    getTvEquipments = (cityDomain, tariffId) => contentRequest.getWithCache(`${cityDomain}/v1/equipments?filter[sale_package_id]=${tariffId}`)
        .then(response => response.data.items)
        .catch(err => captureException(err));

    getAntiviruses = cityDomain => contentRequest.getWithCache(`${cityDomain}/v1/antivirus`, {
            params: {
                fields: 'billing_id,name,description,image,price',
                filter: {
                    is_hit: 1,
                },
            }
        })
        .then(response => response.data.items.map(item => ({
            id: item.billing_id,
            name: item.name,
            description: item.description,
            image: item.image,
            price: item.price,
        })))
        .catch(err => captureException(err));

    getPacketsChannels = cityDomain => getPacketChannelsList(cityDomain, { isActive: 1, onlyCountChannels: 1, withPrices: 1 })
        .then(resp => resp.data.map(item => ({
            id: item.billing_id,
            name: item.title,
            description: item.body,
            price: item.price,
            hdChannelsCount: item.hdChannelsCount,
            sdChannelsCount: item.sdChannelsCount,
            image: process.env.EPG_API_ENDPOINT + item.image680x280,
            alias: item.alias_second,
        })))
        .catch(err => {
            captureException(err);
            return [];
        });

    getPhoneTariff = (cityDomain, tariffId) => contentRequest.getWithCache(`${cityDomain}/v1/adm-telephony`, {
        params: {
            filter: {
                tariff_id: tariffId,
            },
        }
    })
    .then(response => response.data.items?.length ? response.data.items[0] : [])
    .catch(err => {
        captureException(err);
        return [];
    });

    /**
     * Отправка запроса
     * @param {(object|FormData)} data - данные
     * @param {string} domain - домен
     * @param {string} [billingHost] - хост биллинга
     * @returns {Promise}
     */
    static postData = (data, domain, billingHost) => sendRequestByConnection(domain, data, billingHost);

    /**
     * Отправка аналитики
     * @param {(number|string)} billing_code - код ответа биллинга
     */
    static GA = ({ billing_code }) => {
        window.dataLayer = window.dataLayer || [];
        let data = Object.create(null);

        switch (Number(billing_code)) {
            case 0: // заявка создана
                data = {
                    'action': 'yes',
                    'label': '0',
                };
                break;
            case 6: // дома нет в базе
                data = {
                    'action': 'undefined',
                    'label': 'short',
                };
                break;
            case 15: // в доме нет подключенных ИИН и КТВ
            case 24: // аналогично 15
            case 100: // Повторная заявка
            case 101: // ошибочно созданная заявка, необходимо для короткой формы на Набережных Челнах
                data = {
                    'action': 'no',
                    'label': `Произошла ошибка ${billing_code}`,
                };
                break;
            default:
                data = {
                    'action': 'undefined',
                    'label': `Произошла ошибка ${billing_code || 'Код не известен.'}`,
                };
        }

        window.dataLayer.push({
            'event': 'UAevent',
            'category': 'application_form',
            ...data,
        });
    };
}
